import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  writeBatch,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase-config';
import SickLeaveModel, {
  SickLeaveSummary,
  SickLeaveValidation,
  SickLeaveHelpers
} from '../models/SickLeaveModels';

/**
 * SickLeaveService - Handles all sick leave Firestore operations
 * Collection Path: companies/{companyId}/sickLeave/{sickLeaveId}
 *
 * IMPORTANT:
 * - All dates are stored as strings in "YYYY-MM-DD" format to avoid timezone issues
 * - Duration equals the scheduled shift minutes for that day
 * - A sick day is only recorded when the staff member has a scheduled shift on that day
 * - No entitlement system — record-only, year-scoped totals, no carry-over
 * - ALL methods return proper SickLeaveModel instances (not plain objects)
 *
 * SUBSCRIPTION QUERY STRATEGY:
 * Real-time subscriptions query by date range only, using the automatic
 * single-field index on `date`. Filtering by staffId or departmentId is
 * applied client-side after the snapshot is received. This avoids the
 * Firestore composite index requirement that combining `in` on one field
 * with a range filter on a different field creates.
 *
 * CASCADE SYNC: Three internal helpers keep sick leave in sync with shift changes.
 * These are called by ScheduleService after confirmed shift operations and never
 * throw errors to the UI:
 * - deleteSickLeaveForDateIfExists: Removes sick leave when no shifts remain on a date
 * - updateSickLeaveDurationForDate: Updates duration when a shift is created or changed
 * - reconcileSickLeaveWithShifts: Full reconciliation after batch/week schedule saves
 */
class SickLeaveService {
  /**
   * Convert Date object or string to YYYY-MM-DD string format
   * @private
   */
  _toDateString(date) {
    // Already a valid YYYY-MM-DD string
    if (typeof date === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      // Try to parse if it's a different format
      console.warn('Non-standard date string format:', date);
      date = new Date(date);
    }

    // Validate Date object
    if (!(date instanceof Date)) {
      console.error('Not a Date object:', date, 'Type:', typeof date);
      throw new Error(`Invalid date provided: expected Date object, got ${typeof date}`);
    }

    if (isNaN(date.getTime())) {
      console.error('Invalid Date object (NaN):', date);
      throw new Error('Invalid date provided: Date object is invalid (NaN)');
    }

    // Convert to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Add a single sick leave day.
   * Shift gate: only records if the staff member has a scheduled shift on that day.
   * If no shift is found, throws to inform the UI.
   * @returns {SickLeaveModel} - SickLeaveModel instance with all methods
   */
  async addSickLeave(companyId, sickLeaveData) {
    try {
      console.log('Adding sick leave:', { companyId, sickLeaveData });

      // Validate data
      const validation = SickLeaveValidation.validate(sickLeaveData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Ensure date is in string format
      const dateString = this._toDateString(sickLeaveData.date);

      // Check for duplicate
      const duplicate = await this.checkDuplicate(
        companyId,
        sickLeaveData.staffId,
        dateString,
        sickLeaveData.departmentId
      );

      if (duplicate) {
        throw new Error('Für diesen Tag existiert bereits ein Krankmeldungseintrag');
      }

      // Calculate duration from shifts on this date
      const durationMinutes = await this.calculateTotalMinutesFromShifts(
        companyId,
        sickLeaveData.staffId,
        dateString,
        sickLeaveData.departmentId
      );

      console.log(
        `Calculated ${durationMinutes} minutes ` +
        `(${SickLeaveHelpers.formatDurationFromMinutes(durationMinutes)}) ` +
        `for sick leave on ${dateString}`
      );

      // Shift gate: throw if no shift exists on this day
      if (durationMinutes === 0) {
        throw new Error('Kein geplanter Dienst für diesen Tag');
      }

      // Create sick leave model
      const sickLeave = new SickLeaveModel({
        ...sickLeaveData,
        date: dateString,
        durationMinutes,
        createdAt: new Date()
      });

      // Generate deterministic ID — prevents duplicates without a separate query
      const sickLeaveId = SickLeaveHelpers.generateSickLeaveId(
        sickLeave.staffId,
        dateString,
        sickLeave.departmentId
      );

      // Save to Firestore
      const sickLeaveRef = doc(db, 'companies', companyId, 'sickLeave', sickLeaveId);
      await setDoc(sickLeaveRef, sickLeave.toFirestore());

      console.log(`Sick leave added: ${sickLeaveId}`);

      return new SickLeaveModel({ id: sickLeaveId, ...sickLeave });
    } catch (error) {
      console.error('Error adding sick leave:', error);
      throw error;
    }
  }

  /**
   * Add sick leave for a date range.
   *
   * For each date in the range:
   * - Skips dates that already have a sick leave record (duplicate check via
   *   deterministic ID — O(1) per date)
   * - Calculates shift duration for that date
   * - Skips dates with no scheduled shift silently (business rule)
   *
   * If the entire range produces zero valid entries, throws to inform the UI.
   * All valid entries are written in a single atomic batch.
   *
   * @param {string} companyId
   * @param {Object} sickLeaveData - { staffId, departmentId, startDate, endDate, createdBy }
   *   startDate / endDate accepted as YYYY-MM-DD strings or Date objects
   * @returns {{ added: number, skipped: number }}
   */
  async addSickLeaveRange(companyId, sickLeaveData) {
    const { staffId, departmentId, startDate, endDate, createdBy } = sickLeaveData;

    // Normalise start and end to midnight local-time Date objects
    const start = typeof startDate === 'string'
      ? (() => {
          const [y, m, d] = startDate.split('-').map(Number);
          return new Date(y, m - 1, d);
        })()
      : startDate;

    const end = typeof endDate === 'string'
      ? (() => {
          const [y, m, d] = endDate.split('-').map(Number);
          return new Date(y, m - 1, d);
        })()
      : endDate;

    if (start > end) {
      throw new Error('Enddatum muss nach dem Startdatum liegen');
    }

    const toWrite = [];
    let skippedCount = 0;

    // Iterate through each calendar date in the range
    const current = new Date(start);
    while (current <= end) {
      const dateString = this._toDateString(current);

      // Skip duplicates — deterministic ID means setDoc would overwrite,
      // but we check explicitly to keep the skipped count accurate
      const isDuplicate = await this.checkDuplicate(
        companyId,
        staffId,
        dateString,
        departmentId
      );

      if (isDuplicate) {
        skippedCount++;
        current.setDate(current.getDate() + 1);
        continue;
      }

      const durationMinutes = await this.calculateTotalMinutesFromShifts(
        companyId,
        staffId,
        dateString,
        departmentId
      );

      if (durationMinutes === 0) {
        // No shift on this date — skip silently (business rule)
        skippedCount++;
        current.setDate(current.getDate() + 1);
        continue;
      }

      const sickLeave = new SickLeaveModel({
        staffId,
        departmentId,
        date: dateString,
        durationMinutes,
        createdBy,
        createdAt: new Date()
      });

      const sickLeaveId = SickLeaveHelpers.generateSickLeaveId(
        staffId,
        dateString,
        departmentId
      );

      toWrite.push({ id: sickLeaveId, model: sickLeave });
      current.setDate(current.getDate() + 1);
    }

    if (toWrite.length === 0) {
      throw new Error('Kein geplanter Dienst für den gewählten Zeitraum');
    }

    // Single atomic batch write
    const batch = writeBatch(db);
    toWrite.forEach(({ id, model }) => {
      const ref = doc(db, 'companies', companyId, 'sickLeave', id);
      batch.set(ref, model.toFirestore());
    });
    await batch.commit();

    console.log(
      `Sick leave range added: ${toWrite.length} entries written, ` +
      `${skippedCount} skipped`
    );

    return { added: toWrite.length, skipped: skippedCount };
  }

  /**
   * Delete a sick leave record
   */
  async deleteSickLeave(companyId, sickLeaveId) {
    try {
      console.log('Deleting sick leave:', { companyId, sickLeaveId });

      const sickLeaveRef = doc(db, 'companies', companyId, 'sickLeave', sickLeaveId);
      await deleteDoc(sickLeaveRef);

      console.log(`Sick leave deleted: ${sickLeaveId}`);

      return { success: true };
    } catch (error) {
      console.error('Error deleting sick leave:', error);
      throw error;
    }
  }

  /**
   * Get sick leave by ID
   * @returns {SickLeaveModel|null} - SickLeaveModel instance or null if not found
   */
  async getSickLeaveById(companyId, sickLeaveId) {
    try {
      const sickLeaveRef = doc(db, 'companies', companyId, 'sickLeave', sickLeaveId);
      const sickLeaveDoc = await getDoc(sickLeaveRef);

      if (!sickLeaveDoc.exists()) {
        return null;
      }

      return SickLeaveModel.fromFirestore(sickLeaveDoc);
    } catch (error) {
      console.error('Error getting sick leave:', error);
      throw error;
    }
  }

  /**
   * Get all sick leave for a staff member in a date range
   * @returns {Array<SickLeaveModel>} - Array of SickLeaveModel instances
   */
  async getSickLeaveByStaff(companyId, staffId, startDate, endDate) {
    try {
      console.log('getSickLeaveByStaff called with:', {
        companyId,
        staffId,
        startDate: startDate?.toString(),
        endDate: endDate?.toString(),
        startDateType: typeof startDate,
        endDateType: typeof endDate
      });

      const startDateString = this._toDateString(startDate);
      const endDateString = this._toDateString(endDate);

      console.log('Converted dates:', { startDateString, endDateString });

      const sickLeaveRef = collection(db, 'companies', companyId, 'sickLeave');
      const q = query(
        sickLeaveRef,
        where('staffId', '==', staffId),
        where('date', '>=', startDateString),
        where('date', '<=', endDateString),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      const sickLeaveList = snapshot.docs.map(doc => SickLeaveModel.fromFirestore(doc));

      console.log(`Found ${sickLeaveList.length} sick leave record(s) for staff ${staffId}`);
      return sickLeaveList;
    } catch (error) {
      console.error('Error getting sick leave by staff:', error);
      throw error;
    }
  }

  /**
   * Get all sick leave for a department in a date range
   * @returns {Array<SickLeaveModel>} - Array of SickLeaveModel instances
   */
  async getSickLeaveByDepartment(companyId, departmentId, startDate, endDate) {
    try {
      console.log('getSickLeaveByDepartment called with:', {
        companyId,
        departmentId,
        startDate: startDate?.toString(),
        endDate: endDate?.toString(),
        startDateType: typeof startDate,
        endDateType: typeof endDate
      });

      const startDateString = this._toDateString(startDate);
      const endDateString = this._toDateString(endDate);

      console.log('Converted dates:', { startDateString, endDateString });

      const sickLeaveRef = collection(db, 'companies', companyId, 'sickLeave');
      const q = query(
        sickLeaveRef,
        where('departmentId', '==', departmentId),
        where('date', '>=', startDateString),
        where('date', '<=', endDateString),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      const sickLeaveList = snapshot.docs.map(doc => SickLeaveModel.fromFirestore(doc));

      console.log(`Found ${sickLeaveList.length} sick leave record(s) for department ${departmentId}`);
      return sickLeaveList;
    } catch (error) {
      console.error('Error getting sick leave by department:', error);
      throw error;
    }
  }

  /**
   * Get all sick leave for a company in a date range
   * @returns {Array<SickLeaveModel>} - Array of SickLeaveModel instances
   */
  async getSickLeaveByDateRange(companyId, startDate, endDate) {
    try {
      console.log('getSickLeaveByDateRange called with:', {
        companyId,
        startDate: startDate?.toString(),
        endDate: endDate?.toString(),
        startDateType: typeof startDate,
        endDateType: typeof endDate
      });

      const startDateString = this._toDateString(startDate);
      const endDateString = this._toDateString(endDate);

      console.log('Converted dates:', { startDateString, endDateString });

      const sickLeaveRef = collection(db, 'companies', companyId, 'sickLeave');
      const q = query(
        sickLeaveRef,
        where('date', '>=', startDateString),
        where('date', '<=', endDateString),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      const sickLeaveList = snapshot.docs.map(doc => SickLeaveModel.fromFirestore(doc));

      console.log(`Found ${sickLeaveList.length} sick leave record(s) in date range`);
      return sickLeaveList;
    } catch (error) {
      console.error('Error getting sick leave by date range:', error);
      throw error;
    }
  }

  /**
   * Get all sick leave for a specific day
   * @returns {Array<SickLeaveModel>} - Array of SickLeaveModel instances
   */
  async getSickLeaveForDay(companyId, date, departmentIds = null) {
    try {
      const dateString = this._toDateString(date);

      const sickLeaveRef = collection(db, 'companies', companyId, 'sickLeave');

      let q;
      if (departmentIds && departmentIds.length > 0) {
        q = query(
          sickLeaveRef,
          where('departmentId', 'in', departmentIds),
          where('date', '==', dateString)
        );
      } else {
        q = query(
          sickLeaveRef,
          where('date', '==', dateString)
        );
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => SickLeaveModel.fromFirestore(doc));
    } catch (error) {
      console.error('Error getting sick leave for day:', error);
      throw error;
    }
  }

  /**
   * Check if a sick leave record already exists for a staff member on a specific date.
   * Uses the deterministic doc ID for an O(1) existence check.
   */
  async checkDuplicate(companyId, staffId, date, departmentId) {
    try {
      const dateString = this._toDateString(date);
      const sickLeaveId = SickLeaveHelpers.generateSickLeaveId(staffId, dateString, departmentId);
      const sickLeaveRef = doc(db, 'companies', companyId, 'sickLeave', sickLeaveId);
      const sickLeaveDoc = await getDoc(sickLeaveRef);
      return sickLeaveDoc.exists();
    } catch (error) {
      console.error('Error checking duplicate:', error);
      return false;
    }
  }

  /**
   * Calculate TOTAL minutes from ALL shifts for a staff member on a specific date.
   * Returns 0 if no shifts are found — caller decides how to handle that case.
   *
   * Query strategy: filter by `date` only — uses the auto-created single-field
   * index and avoids a composite index requirement on the shifts subcollection.
   * The departmentId on the sick leave record is used for categorisation and
   * reporting only; a staff member is sick on a day regardless of which
   * department the shift belongs to. This is consistent with the cascade sync
   * helpers (deleteSickLeaveForDateIfExists, updateSickLeaveDurationForDate,
   * reconcileSickLeaveWithShifts) which also never filter shifts by departmentId.
   *
   * Field priority:
   * 1. totalMinutes (most common — raw Firestore shift field)
   * 2. durationMinutes (alternative field name)
   * 3. expectedHours (converted to minutes)
   * 4. startTime / endTime string diff (last resort — "HH:MM" format)
   *
   * @param {string} companyId - Company ID
   * @param {string} staffId - Staff ID
   * @param {string|Date} date - Date in YYYY-MM-DD format or Date object
   * @param {string} departmentId - Accepted for API consistency; not used in query
   * @returns {number} Total minutes from all shifts on that date (0 if none found)
   */
  async calculateTotalMinutesFromShifts(companyId, staffId, date, departmentId) {
    try {
      const dateStr = this._toDateString(date);

      console.log('Calculating minutes from shifts:', {
        companyId,
        staffId,
        dateStr,
        departmentId
      });

      const shiftsRef = collection(db, 'companies', companyId, 'schedules', staffId, 'shifts');

      // Query by date only — avoids a composite index requirement on the shifts
      // subcollection. departmentId is intentionally excluded from this query;
      // see JSDoc above for the full rationale.
      const q = query(
        shiftsRef,
        where('date', '==', dateStr)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log(`No shifts found for ${staffId} on ${dateStr}`);
        return 0;
      }

      console.log(`Found ${snapshot.size} shift(s) to process`);

      let totalMinutes = 0;

      snapshot.docs.forEach((doc, index) => {
        const shiftData = doc.data();
        let minutes = 0;

        console.log(`  Shift ${index + 1}:`, {
          id: doc.id,
          totalMinutes: shiftData.totalMinutes,
          durationMinutes: shiftData.durationMinutes,
          expectedHours: shiftData.expectedHours,
          startTime: shiftData.startTime,
          endTime: shiftData.endTime
        });

        // Priority 1: totalMinutes (most common)
        if (shiftData.totalMinutes !== undefined && shiftData.totalMinutes !== null) {
          minutes = Number(shiftData.totalMinutes) || 0;
          console.log(`    Using totalMinutes: ${minutes} min`);
        }
        // Priority 2: durationMinutes (alternative field name)
        else if (shiftData.durationMinutes !== undefined && shiftData.durationMinutes !== null) {
          minutes = Number(shiftData.durationMinutes) || 0;
          console.log(`    Using durationMinutes: ${minutes} min`);
        }
        // Priority 3: expectedHours converted to minutes
        else if (shiftData.expectedHours !== undefined && shiftData.expectedHours !== null) {
          minutes = Math.round(Number(shiftData.expectedHours) * 60) || 0;
          console.log(`    Using expectedHours: ${shiftData.expectedHours}h = ${minutes} min`);
        }
        // Priority 4: startTime / endTime "HH:MM" string diff
        else if (shiftData.startTime && shiftData.endTime) {
          try {
            const [startH, startM] = shiftData.startTime.split(':').map(Number);
            const [endH, endM] = shiftData.endTime.split(':').map(Number);
            const startTotal = startH * 60 + startM;
            const endTotal = endH * 60 + endM;
            minutes = endTotal > startTotal ? endTotal - startTotal : 0;
            console.log(`    Calculated from HH:MM strings: ${minutes} min`);
          } catch (err) {
            console.warn(`    Failed to parse startTime/endTime strings:`, err);
          }
        } else {
          console.warn(`    Could not determine minutes from shift data`);
        }

        totalMinutes += minutes;
      });

      console.log(
        `Total minutes calculated: ${totalMinutes} min ` +
        `(${SickLeaveHelpers.formatDurationFromMinutes(totalMinutes)}) ` +
        `from ${snapshot.size} shift(s)`
      );
      return totalMinutes;

    } catch (error) {
      console.error('Error calculating total minutes from shifts:', error);
      return 0;
    }
  }

  /**
   * Get sick leave summary statistics for a full calendar year.
   * Totals naturally reset each calendar year — no extra logic required.
   * No entitlement or carry-over fields.
   *
   * @param {string} companyId - Company ID
   * @param {number} year - Four-digit year (e.g. 2025)
   * @param {Array<string>|null} departmentIds - Optional department filter
   * @param {Object} staffMap - Map of staffId -> staff object (for name lookup)
   * @returns {SickLeaveSummary}
   */
  async getYearSummary(companyId, year, departmentIds = null, staffMap = {}) {
    try {
      const startDate = new Date(year, 0, 1);
      const endDate = new Date(year, 11, 31, 23, 59, 59, 999);

      let sickLeaveList;

      if (departmentIds && departmentIds.length > 0) {
        const allSickLeave = [];
        for (const deptId of departmentIds) {
          const deptSickLeave = await this.getSickLeaveByDepartment(
            companyId,
            deptId,
            startDate,
            endDate
          );
          allSickLeave.push(...deptSickLeave);
        }
        sickLeaveList = allSickLeave;
      } else {
        sickLeaveList = await this.getSickLeaveByDateRange(companyId, startDate, endDate);
      }

      const summary = new SickLeaveSummary();

      sickLeaveList.forEach(sickLeave => {
        const staff = staffMap[sickLeave.staffId];
        const staffName = staff ? staff.name : 'Unbekannter Mitarbeiter';
        summary.addSickLeave(sickLeave, staffName);
      });

      return summary;
    } catch (error) {
      console.error('Error getting year summary:', error);
      throw error;
    }
  }

  /**
   * Subscribe to sick leave changes in a date range (real-time listener).
   *
   * Queries by date range only — using the automatic single-field index on
   * `date`. When departmentIds is provided, filtering is applied client-side
   * after the snapshot is received. This avoids the Firestore composite index
   * requirement that the previous `in` on departmentId + range on date
   * combination created.
   *
   * @param {Function} callback - Receives array of SickLeaveModel instances
   * @returns {Function} - Unsubscribe function
   */
  subscribeToSickLeave(companyId, startDate, endDate, callback, departmentIds = null) {
    try {
      const startDateString = this._toDateString(startDate);
      const endDateString = this._toDateString(endDate);

      const sickLeaveRef = collection(db, 'companies', companyId, 'sickLeave');

      // Date range only — single-field index on `date`, no composite index needed.
      const q = query(
        sickLeaveRef,
        where('date', '>=', startDateString),
        where('date', '<=', endDateString),
        orderBy('date', 'asc')
      );

      // Build a Set once for O(1) lookup per document in the callback.
      const departmentIdSet =
        departmentIds && departmentIds.length > 0
          ? new Set(departmentIds)
          : null;

      return onSnapshot(
        q,
        (snapshot) => {
          let sickLeaveList = snapshot.docs.map(doc =>
            SickLeaveModel.fromFirestore(doc)
          );
          if (departmentIdSet) {
            sickLeaveList = sickLeaveList.filter(entry =>
              departmentIdSet.has(entry.departmentId)
            );
          }
          callback(sickLeaveList);
        },
        (error) => {
          console.error('Error in sick leave subscription:', error);
          callback([]);
        }
      );
    } catch (error) {
      console.error('Error subscribing to sick leave:', error);
      throw error;
    }
  }

  /**
   * Subscribe to sick leave for a specific set of staff members in a date range.
   *
   * Queries by date range only — using the automatic single-field index on
   * `date`. staffId filtering is applied client-side after the snapshot is
   * received. This avoids the Firestore composite index requirement that the
   * previous `in` on staffId + range on date combination created.
   *
   * If staffIds is empty, callback is called immediately with [] and a no-op
   * unsubscribe function is returned.
   *
   * @param {string} companyId
   * @param {Date} startDate
   * @param {Date} endDate
   * @param {Array<string>} staffIds
   * @param {Function} callback - Receives array of SickLeaveModel instances
   * @returns {Function} - Unsubscribe function
   */
  subscribeToSickLeaveByStaffIds(companyId, startDate, endDate, staffIds, callback) {
    if (!staffIds || staffIds.length === 0) {
      callback([]);
      return () => {};
    }

    try {
      const startDateString = this._toDateString(startDate);
      const endDateString = this._toDateString(endDate);

      const sickLeaveRef = collection(db, 'companies', companyId, 'sickLeave');

      // Date range only — single-field index on `date`, no composite index needed.
      // staffId filtering is done client-side below.
      const q = query(
        sickLeaveRef,
        where('date', '>=', startDateString),
        where('date', '<=', endDateString)
      );

      // Build a Set once for O(1) lookup per document in the callback.
      const staffIdSet = new Set(staffIds);

      return onSnapshot(
        q,
        (snapshot) => {
          const sickLeaveList = snapshot.docs
            .map(doc => SickLeaveModel.fromFirestore(doc))
            .filter(entry => staffIdSet.has(entry.staffId))
            .sort((a, b) => {
              const dateA = a.getDateString ? a.getDateString() : a.date;
              const dateB = b.getDateString ? b.getDateString() : b.date;
              return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
            });
          callback(sickLeaveList);
        },
        (error) => {
          console.error('Error in sick leave by staff subscription:', error);
          callback([]);
        }
      );
    } catch (error) {
      console.error('Error subscribing to sick leave by staff IDs:', error);
      throw error;
    }
  }

  /**
   * Subscribe to sick leave for a specific day (real-time listener)
   * @param {Function} callback - Receives array of SickLeaveModel instances
   * @returns {Function} - Unsubscribe function
   */
  subscribeToSickLeaveForDay(companyId, date, callback, departmentIds = null) {
    try {
      const dateString = this._toDateString(date);

      const sickLeaveRef = collection(db, 'companies', companyId, 'sickLeave');

      let q;
      if (departmentIds && departmentIds.length > 0) {
        q = query(
          sickLeaveRef,
          where('departmentId', 'in', departmentIds),
          where('date', '==', dateString)
        );
      } else {
        q = query(
          sickLeaveRef,
          where('date', '==', dateString)
        );
      }

      return onSnapshot(
        q,
        (snapshot) => {
          const sickLeaveList = snapshot.docs.map(doc =>
            SickLeaveModel.fromFirestore(doc)
          );
          callback(sickLeaveList);
        },
        (error) => {
          console.error('Error in sick leave subscription for day:', error);
          callback([]);
        }
      );
    } catch (error) {
      console.error('Error subscribing to sick leave for day:', error);
      throw error;
    }
  }

  /**
   * Group sick leave by date
   * @param {Array<SickLeaveModel>} sickLeaveList
   * @returns {Map<string, Array<SickLeaveModel>>}
   */
  groupSickLeaveByDate(sickLeaveList) {
    const grouped = new Map();

    sickLeaveList.forEach(sickLeave => {
      const dateStr = sickLeave.getDateString();
      if (!grouped.has(dateStr)) {
        grouped.set(dateStr, []);
      }
      grouped.get(dateStr).push(sickLeave);
    });

    return grouped;
  }

  /**
   * Group sick leave by staff
   * @param {Array<SickLeaveModel>} sickLeaveList
   * @returns {Map<string, Array<SickLeaveModel>>}
   */
  groupSickLeaveByStaff(sickLeaveList) {
    const grouped = new Map();

    sickLeaveList.forEach(sickLeave => {
      if (!grouped.has(sickLeave.staffId)) {
        grouped.set(sickLeave.staffId, []);
      }
      grouped.get(sickLeave.staffId).push(sickLeave);
    });

    return grouped;
  }

  // ==================== CASCADE SYNC HELPERS ====================
  // Called by ScheduleService after confirmed shift operations.
  // Never throw errors to the UI — fail silently with console.error only.
  // Query by staffId + date (not deterministic ID) because departmentId
  // is not available in the shift cascade context.

  /**
   * Delete sick leave document(s) for a staff member on a specific date.
   * Called when a shift is deleted and no other shifts remain on that date.
   * Does nothing if no sick leave documents are found.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string} dateString - YYYY-MM-DD
   */
  async deleteSickLeaveForDateIfExists(companyId, staffId, dateString) {
    try {
      const sickLeaveRef = collection(db, 'companies', companyId, 'sickLeave');
      const q = query(
        sickLeaveRef,
        where('staffId', '==', staffId),
        where('date', '==', dateString)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log(
          `Cascade: no sick leave found for staff ${staffId} on ${dateString} — nothing to delete`
        );
        return;
      }

      const batch = writeBatch(db);
      snapshot.docs.forEach(sickLeaveDoc => {
        batch.delete(sickLeaveDoc.ref);
      });
      await batch.commit();

      console.log(
        `Cascade: deleted ${snapshot.size} sick leave record(s) for staff ${staffId} on ${dateString}`
      );
    } catch (error) {
      console.error(
        `Cascade error - deleteSickLeaveForDateIfExists (staffId: ${staffId}, date: ${dateString}):`,
        error
      );
    }
  }

  /**
   * Update durationMinutes on existing sick leave document(s) for a specific date.
   * Called when a shift is created or updated on a date that already has a sick
   * leave record. Does nothing if no sick leave documents are found.
   * Never creates new sick leave documents.
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string} dateString - YYYY-MM-DD
   * @param {number} newDurationMinutes
   */
  async updateSickLeaveDurationForDate(companyId, staffId, dateString, newDurationMinutes) {
    try {
      const sickLeaveRef = collection(db, 'companies', companyId, 'sickLeave');
      const q = query(
        sickLeaveRef,
        where('staffId', '==', staffId),
        where('date', '==', dateString)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log(
          `Cascade: no sick leave found for staff ${staffId} on ${dateString} — skipping duration update`
        );
        return;
      }

      const batch = writeBatch(db);
      snapshot.docs.forEach(sickLeaveDoc => {
        batch.update(sickLeaveDoc.ref, {
          durationMinutes: newDurationMinutes
        });
      });
      await batch.commit();

      console.log(
        `Cascade: updated sick leave duration for staff ${staffId} on ${dateString} ` +
        `to ${newDurationMinutes} min`
      );
    } catch (error) {
      console.error(
        `Cascade error - updateSickLeaveDurationForDate ` +
        `(staffId: ${staffId}, date: ${dateString}, minutes: ${newDurationMinutes}):`,
        error
      );
    }
  }

  /**
   * Reconcile all sick leave documents for a staff member against a set of
   * active shifts within a date range.
   *
   * Used after full week schedule saves (DELETE-then-CREATE pattern) and after
   * batch shift deletions. Performs a single atomic Firestore batch that:
   * - Updates durationMinutes on sick leave whose date exists in activeShifts
   *   (if value differs)
   * - Deletes sick leave whose date has no corresponding shift in activeShifts
   *   (orphan removal)
   *
   * Multiple shifts on the same day are summed correctly via the lookup map.
   *
   * IMPORTANT:
   * - Never creates new sick leave documents
   * - Uses a single Firestore batch for atomicity
   * - Never throws to the UI
   *
   * @param {string} companyId
   * @param {string} staffId
   * @param {string} startDate - YYYY-MM-DD (inclusive)
   * @param {string} endDate - YYYY-MM-DD (inclusive)
   * @param {Array<Object>} activeShifts - Each object must have:
   *   - date: string (YYYY-MM-DD)
   *   - totalMinutes: number  OR  expectedHours: number (fallback)
   *   Pass an empty array to delete all sick leave in the range (full week cleared).
   */
  async reconcileSickLeaveWithShifts(companyId, staffId, startDate, endDate, activeShifts) {
    try {
      // Build lookup map: { dateString: totalMinutes }
      // Sum per date to correctly handle multiple shifts on the same day
      const shiftMinutesMap = {};
      activeShifts.forEach(shift => {
        const date = shift.date;
        if (!date) return;

        const minutes = typeof shift.totalMinutes === 'number'
          ? shift.totalMinutes
          : Math.round((shift.expectedHours || 0) * 60);

        shiftMinutesMap[date] = (shiftMinutesMap[date] || 0) + minutes;
      });

      // Query all sick leave for this staff member within the date range
      const sickLeaveRef = collection(db, 'companies', companyId, 'sickLeave');
      const q = query(
        sickLeaveRef,
        where('staffId', '==', staffId),
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log(
          `Cascade: no sick leave found for staff ${staffId} ` +
          `between ${startDate} and ${endDate} — nothing to reconcile`
        );
        return;
      }

      const batch = writeBatch(db);
      let updateCount = 0;
      let deleteCount = 0;
      let unchangedCount = 0;

      snapshot.docs.forEach(sickLeaveDoc => {
        const sickLeave = sickLeaveDoc.data();
        const dateString = sickLeave.date;

        if (shiftMinutesMap[dateString] !== undefined) {
          // A shift exists for this date — update duration only if value has changed
          const newMinutes = shiftMinutesMap[dateString];
          if (sickLeave.durationMinutes !== newMinutes) {
            batch.update(sickLeaveDoc.ref, {
              durationMinutes: newMinutes
            });
            updateCount++;
          } else {
            unchangedCount++;
          }
        } else {
          // No shift exists for this date — delete the orphaned sick leave record
          batch.delete(sickLeaveDoc.ref);
          deleteCount++;
        }
      });

      if (updateCount > 0 || deleteCount > 0) {
        await batch.commit();
        console.log(
          `Cascade: reconciled sick leave for staff ${staffId} ` +
          `(${startDate} to ${endDate}): ` +
          `${updateCount} updated, ${deleteCount} deleted, ${unchangedCount} unchanged`
        );
      } else {
        console.log(
          `Cascade: no sick leave changes needed for staff ${staffId} ` +
          `(${startDate} to ${endDate}) — all ${unchangedCount} record(s) already in sync`
        );
      }
    } catch (error) {
      console.error(
        `Cascade error - reconcileSickLeaveWithShifts ` +
        `(staffId: ${staffId}, range: ${startDate} to ${endDate}):`,
        error
      );
    }
  }
}

// Export singleton instance
export default new SickLeaveService();